import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import useSWR from "swr";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Globe, Loader2, Lock, PartyPopper, ShieldCheck, ShoppingBag } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { API, fetcher } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useCart } from "../context/CartContext";
import { COUNTRY_GROUPS, DELIVERY_RATES, deliveryFor, deliveryZone } from "../lib/delivery";

const FIELDS = [
  { key: "name", label: "Full name", placeholder: "Alex Sparkles", type: "text" },
  { key: "email", label: "Email", placeholder: "you@example.com", type: "email" },
  { key: "address", label: "Address", placeholder: "12 Rainbow Road", type: "text" },
  { key: "city", label: "City", placeholder: "London", type: "text" },
  { key: "postcode", label: "Postcode / ZIP", placeholder: "SW1A 1AA or ZIP", type: "text" },
  { key: "country", label: "Country", type: "select" },
];

export default function CheckoutPage() {
  useSeo({
    title: "Secure Checkout — Card & PayPal",
    description:
      "Secure Sparkwear checkout — pay by card or PayPal. Free UK delivery on children's clothing orders over £50.",
  });
  const { items, subtotal, clear } = useCart();
  const { data: ppConfig, isLoading: ppLoading } = useSWR(
    `${API}/paypal/config`,
    fetcher,
    { revalidateOnFocus: false, revalidateIfStale: false, dedupingInterval: 60000 }
  );
  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
  });
  const [placing, setPlacing] = useState(false);
  const [orderRef, setOrderRef] = useState(null);

  const zone = DELIVERY_RATES[deliveryZone(form.country)];
  const delivery = deliveryFor(subtotal, form.country);
  const total = subtotal + delivery;
  const paypalClientId = ppConfig?.configured ? ppConfig.client_id : null;
  const paypalOptions = useMemo(
    () => ({ clientId: paypalClientId, currency: "GBP" }),
    [paypalClientId]
  );
  const formOk = FIELDS.every((f) => form[f.key].trim().length > 0);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const lineItems = () =>
    items.map((i) => ({ productId: i.productId, size: i.size, colour: i.colour || "", qty: i.qty }));

  const demoPay = async () => {
    if (!formOk) {
      toast.error("Please fill in your delivery details first.");
      return;
    }
    setPlacing(true);
    try {
      const res = await axios.post(`${API}/checkout/demo`, { ...form, items: lineItems() });
      setOrderRef(res.data.order_id);
      clear();
      toast.success("Demo order placed — thank you!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place the demo order.");
    } finally {
      setPlacing(false);
    }
  };

  const startStripe = async () => {
    if (!formOk) {
      toast.error("Please fill in your delivery details first.");
      return;
    }
    setPlacing(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: lineItems(),
        contact: form,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the card payment.");
      setPlacing(false);
    }
  };

  if (orderRef) {
    return (
      <main className="flex min-h-screen items-center justify-center pb-24 pt-28" data-testid="order-success">
        <div className="mx-auto max-w-md px-5 text-center">
          <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-mint-50">
            <PartyPopper className="h-9 w-9 text-mint-600" />
          </span>
          <h1 className="mt-6 font-display text-4xl font-semibold text-ink">
            Order placed!
          </h1>
          <p className="mt-3 text-muted">
            Your Sparkwear order reference is{" "}
            <strong className="text-ink" data-testid="order-ref">{orderRef}</strong>. A
            confirmation is on its way to {form.email || "your inbox"}.
          </p>
          <Link
            to="/shop"
            data-testid="order-success-shop-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
          >
            Keep Shopping <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="flex min-h-screen items-center justify-center pb-24 pt-28" data-testid="checkout-empty-state">
        <div className="mx-auto max-w-md px-5 text-center">
          <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-blush-50">
            <ShoppingBag className="h-9 w-9 text-blush-500" />
          </span>
          <h1 className="mt-6 font-display text-4xl font-semibold text-ink">
            Nothing to check out yet
          </h1>
          <p className="mt-3 text-muted">Your bag is empty — let's fix that.</p>
          <Link
            to="/shop"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
          >
            Shop the Collection <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pb-24 pt-28 md:pt-36" data-testid="checkout-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
            Almost There
          </p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">
            Checkout
          </h1>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <form
              data-testid="checkout-details-form"
              className="rounded-[32px] border border-lav-100 bg-white p-7 md:p-8"
              onSubmit={(e) => e.preventDefault()}
            >
              <h2 className="font-display text-2xl font-semibold text-ink">
                Delivery Details
              </h2>
              <p className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-muted" data-testid="checkout-worldwide-note">
                <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blush-500" />
                <span>
                  We deliver worldwide — UK £4.99 (free over £50) · Europe £6.99 (free over £80) · rest of the world £8.99 (free over £100). Paying from abroad? PayPal and your card show the total in your own currency automatically.
                </span>
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <label key={f.key} className={`block ${f.key === "address" || f.key === "country" ? "sm:col-span-2" : ""}`}>
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">
                      {f.label}
                    </span>
                    {f.key === "country" ? (
                      <select
                        value={form.country}
                        onChange={set("country")}
                        data-testid="checkout-field-country"
                        className="mt-2 w-full appearance-none rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
                      >
                        {COUNTRY_GROUPS.map((g) => (
                          <optgroup key={g.label} label={g.label}>
                            {g.options.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={f.type}
                        value={form[f.key]}
                        onChange={set(f.key)}
                        placeholder={f.placeholder}
                        data-testid={`checkout-field-${f.key}`}
                        className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-muted/50 focus:border-blush-500"
                      />
                    )}
                  </label>
                ))}
              </div>
            </form>

            <div
              data-testid="checkout-payment-card"
              className="mt-6 rounded-[32px] border border-lav-100 bg-white p-7 md:p-8"
            >
              <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-ink">
                <Lock className="h-5 w-5 text-mint-600" /> Payment
              </h2>

              <div data-testid="stripe-card-payment">
                <button
                  onClick={startStripe}
                  disabled={placing}
                  data-testid="stripe-pay-button"
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#635BFF] py-4 font-display text-base font-semibold text-white shadow-lg transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70"
                >
                  {placing ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {placing ? "Opening secure checkout..." : `Pay £${total.toFixed(2)} by card`}
                </button>
                <p className="mt-2 text-xs text-muted">
                  Secure card payment powered by Stripe · Visa, Mastercard, Amex · pay in your own currency from anywhere.
                </p>
              </div>

              <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.14em] text-muted/70">
                <span className="h-px flex-1 bg-lav-100" />
                or pay with PayPal
                <span className="h-px flex-1 bg-lav-100" />
              </div>

              {ppLoading ? (
                <div className="mt-6 h-24 animate-pulse rounded-2xl bg-blush-50" data-testid="paypal-loading" />
              ) : paypalClientId ? (
                <div className="mt-6" data-testid="paypal-buttons-container">
                  <PayPalScriptProvider options={paypalOptions}>
                    <PayPalButtons
                      style={{ layout: "vertical", borderRadius: 24 }}
                      createOrder={async () => {
                        const res = await axios.post(`${API}/paypal/orders`, {
                          ...form,
                          items: lineItems(),
                        });
                        return res.data.id;
                      }}
                      onApprove={async (data) => {
                        try {
                          const res = await axios.post(
                            `${API}/paypal/orders/${data.orderID}/capture`,
                            { contact: form }
                          );
                          setOrderRef(res.data.order_id || data.orderID);
                          clear();
                          toast.success("Payment complete — thank you!");
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.detail || "Payment capture failed — please contact us."
                          );
                        }
                      }}
                      onError={() => toast.error("PayPal could not start — please try again.")}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <div className="mt-6" data-testid="paypal-demo-mode">
                  <p className="rounded-2xl bg-butter-50 p-4 text-xs leading-relaxed text-ink/70">
                    <strong>Demo mode:</strong> PayPal activates automatically once
                    your PayPal client ID is added to the website settings. Placing
                    this order now records it without taking payment.
                  </p>
                  <button
                    onClick={demoPay}
                    disabled={placing}
                    data-testid="demo-paypal-button"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#0070BA] py-4 font-display text-base font-semibold text-white shadow-lg transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70"
                  >
                    {placing ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                    {placing ? "Placing order..." : `Pay £${total.toFixed(2)} with PayPal`}
                  </button>
                </div>
              )}

              <p className="mt-4 flex items-center gap-2 text-xs text-muted">
                <ShieldCheck className="h-4 w-4 text-mint-600" />
                Secure checkout · 30-day happy returns · Free UK delivery over £50 · we deliver worldwide
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div
              data-testid="checkout-summary-card"
              className="rounded-[32px] bg-gradient-to-br from-blush-100 via-lav-50 to-baby-50 p-7 md:p-8"
            >
              <h2 className="font-display text-2xl font-semibold text-ink">
                Your Order
              </h2>
              <ul className="mt-5 space-y-3">
                {items.map((it) => (
                  <li
                    key={`${it.productId}:${it.size}:${it.colour || ""}`}
                    className="flex items-center gap-3 rounded-2xl bg-white/80 p-3"
                  >
                    {it.image ? (
                      <img
                        src={it.image}
                        alt={it.name}
                        className="h-14 w-12 shrink-0 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-12 shrink-0 items-center justify-center rounded-xl bg-lav-50">
                        <ShoppingBag className="h-5 w-5 text-lav-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink">{it.name}</p>
                      <p className="text-xs text-muted">
                        {it.colour ? `${it.colour} · ` : ""}Size {it.size} · Qty {it.qty}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-ink">
                      £{(it.price * it.qty).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
              <dl className="mt-6 space-y-3 border-t border-ink/10 pt-5 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-muted">Subtotal</dt>
                  <dd data-testid="checkout-subtotal" className="font-semibold text-ink">
                    £{subtotal.toFixed(2)}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted">{zone.label}</dt>
                  <dd data-testid="checkout-delivery" className="font-semibold text-ink">
                    {delivery === 0 ? "FREE" : `£${delivery.toFixed(2)}`}
                  </dd>
                </div>
                <div className="flex items-center justify-between border-t border-ink/10 pt-3 text-base">
                  <dt className="font-display font-semibold text-ink">Total</dt>
                  <dd data-testid="checkout-total" className="font-display text-2xl font-semibold text-ink">
                    £{total.toFixed(2)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
